import { 
  AssignedFeature,
  Feature, 
  FeatureCreate, 
  FeatureGroup,
  FeatureGroupCreate,
  FeatureGroupUpdate,
  FeatureUpdate, 
  PlanWithFeatures, 
  PlanFeatureAssignment, 
  PlanFeatureAssignmentMetadataUpdate,
  BulkPlanFeatureAssignment, 
  FeatureAssignmentSummary 
} from '../types';

class FeatureService {
  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const token = localStorage.getItem('token');
    
    // Use relative URLs to leverage the proxy configuration
    const response = await fetch(endpoint, {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  // Feature CRUD operations
  async getFeatures(params?: {
    skip?: number;
    limit?: number;
    category?: string;
    feature_group_id?: number;
    comparison_only?: boolean;
    is_active?: boolean;
  }): Promise<Feature[]> {
    const searchParams = new URLSearchParams();
    if (params?.skip !== undefined) searchParams.append('skip', params.skip.toString());
    if (params?.limit !== undefined) searchParams.append('limit', params.limit.toString());
    if (params?.category) searchParams.append('category', params.category);
    if (params?.feature_group_id !== undefined) {
      searchParams.append('feature_group_id', params.feature_group_id.toString());
    }
    if (params?.comparison_only !== undefined) {
      searchParams.append('comparison_only', params.comparison_only.toString());
    }
    if (params?.is_active !== undefined) searchParams.append('is_active', params.is_active.toString());

    const queryString = searchParams.toString();
    return this.request<Feature[]>(`/api/v1/features/${queryString ? `?${queryString}` : ''}`);
  }

  async getFeature(featureId: number): Promise<Feature> {
    return this.request<Feature>(`/api/v1/features/${featureId}`);
  }

  async getFeatureGroups(params?: {
    skip?: number;
    limit?: number;
    is_active?: boolean;
  }): Promise<FeatureGroup[]> {
    const searchParams = new URLSearchParams();
    if (params?.skip !== undefined) searchParams.append('skip', params.skip.toString());
    if (params?.limit !== undefined) searchParams.append('limit', params.limit.toString());
    if (params?.is_active !== undefined) {
      searchParams.append('is_active', params.is_active.toString());
    }

    const queryString = searchParams.toString();
    return this.request<FeatureGroup[]>(
      `/api/v1/features/feature-groups/${queryString ? `?${queryString}` : ''}`,
    );
  }

  async createFeatureGroup(featureGroup: FeatureGroupCreate): Promise<FeatureGroup> {
    return this.request<FeatureGroup>('/api/v1/features/feature-groups/', {
      method: 'POST',
      body: JSON.stringify(featureGroup),
    });
  }

  async updateFeatureGroup(
    featureGroupId: number,
    featureGroup: FeatureGroupUpdate,
  ): Promise<FeatureGroup> {
    return this.request<FeatureGroup>(`/api/v1/features/feature-groups/${featureGroupId}`, {
      method: 'PUT',
      body: JSON.stringify(featureGroup),
    });
  }

  async deleteFeatureGroup(featureGroupId: number): Promise<void> {
    await this.request<void>(`/api/v1/features/feature-groups/${featureGroupId}`, {
      method: 'DELETE',
    });
  }

  async createFeature(feature: FeatureCreate): Promise<Feature> {
    return this.request<Feature>('/api/v1/features/', {
      method: 'POST',
      body: JSON.stringify(feature),
    });
  }

  async updateFeature(featureId: number, feature: FeatureUpdate): Promise<Feature> {
    return this.request<Feature>(`/api/v1/features/${featureId}`, {
      method: 'PUT',
      body: JSON.stringify(feature),
    });
  }

  async deleteFeature(featureId: number): Promise<void> {
    await this.request<void>(`/api/v1/features/${featureId}`, {
      method: 'DELETE',
    });
  }

  // Feature categories
  async getFeatureCategories(): Promise<string[]> {
    return this.request<string[]>('/api/v1/features/categories/');
  }

  // Plan-Feature assignment operations
  async assignFeatureToPlan(assignment: PlanFeatureAssignment): Promise<any> {
    return this.request<any>('/api/v1/features/assign', {
      method: 'POST',
      body: JSON.stringify(assignment),
    });
  }

  async bulkAssignFeaturesToPlan(assignment: BulkPlanFeatureAssignment): Promise<any[]> {
    return this.request<any[]>('/api/v1/features/assign/bulk', {
      method: 'POST',
      body: JSON.stringify(assignment),
    });
  }

  async unassignFeatureFromPlan(assignment: PlanFeatureAssignment): Promise<void> {
    await this.request<void>('/api/v1/features/unassign', {
      method: 'DELETE',
      body: JSON.stringify(assignment),
    });
  }

  async bulkUnassignFeaturesFromPlan(assignment: BulkPlanFeatureAssignment): Promise<void> {
    await this.request<void>('/api/v1/features/unassign/bulk', {
      method: 'DELETE',
      body: JSON.stringify(assignment),
    });
  }

  // Plan-Feature relationship queries
  async getPlanWithFeatures(planId: number): Promise<PlanWithFeatures> {
    return this.request<PlanWithFeatures>(`/api/v1/features/plans/${planId}`);
  }

  async updatePlanFeatureAssignment(
    planId: number,
    featureId: number,
    payload: PlanFeatureAssignmentMetadataUpdate,
  ): Promise<AssignedFeature> {
    return this.request<AssignedFeature>(`/api/v1/features/plans/${planId}/features/${featureId}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  async getPlanFeatureSummary(planId: number): Promise<FeatureAssignmentSummary> {
    return this.request<FeatureAssignmentSummary>(`/api/v1/features/plans/${planId}/summary`);
  }

  async getAvailableFeaturesForPlan(planId: number): Promise<Feature[]> {
    return this.request<Feature[]>(`/api/v1/features/plans/${planId}/available`);
  }
}

export const featureService = new FeatureService();
export default featureService;
